import React, { useContext, useMemo, useState } from 'react';
import {
    CrUpdateFormState, IProjectUpdate, IProject,
    ProjectUpdate, Project, ProgressUpdateValidations, IEntityProgressUpdateFormProps,
    IPPAppProjectUpdateFinancial, ICustomerFinancialYear
} from '../../types';
import styles from '../../styles/cr.module.scss';
import { RagPicker } from '../cr/RagPicker';
import { RagWithComments } from '../cr/RagWithComments';
import { CrTextField } from '../cr/CrTextField';
import { ProgressUpdateForm } from '../ProgressUpdateForm';
import { CrDropdown } from '../cr/CrDropdown';
import { LookupService, ValidationService, EntityPeopleService, CustomFieldService, NumberService, DateService } from '../../services';
import { CrNumberTextField } from '../cr/CrNumberTextField';
import { CrCheckbox } from '../cr/CrCheckbox';
import { CrLabel } from '../cr/CrLabel';
import { DataContext } from '../DataContext';
import { CrDatePicker } from '../cr/CrDatePicker';
import { ConfirmDialog } from '../cr/ConfirmDialog';

export class ProjectProgressUpdateFormValidations extends ProgressUpdateValidations {
    public StartDate: string = null;
    public EndDate: string = null;
}

export class ProjectProgressUpdateFormState extends CrUpdateFormState<IProjectUpdate, ProjectProgressUpdateFormValidations, IProject> {
    constructor(projectId: number, period: Date, parentEntity?: IProject, showForm?: boolean) {
        super(
            new ProjectUpdate(projectId, period),
            parentEntity || new Project(),
            new ProjectUpdate(),
            new ProjectProgressUpdateFormValidations(),
            showForm
        );
    }
}

// --- Financial year helpers (mirrors ProjectForm / ProjectFinancialUpdateForm) --------
const getFYYearForDate = (date: Date, fyStartMonth: number, fyStartDay: number): number => {
    const month = date.getMonth();
    const day = date.getDate();
    if (month > fyStartMonth || (month === fyStartMonth && day >= fyStartDay)) {
        return date.getFullYear();
    }
    return date.getFullYear() - 1;
};

const getFYLabelFromYear = (fyStartYear: number): string => {
    const startYY = (fyStartYear % 100).toString().padStart(2, '0');
    const endYY = ((fyStartYear + 1) % 100).toString().padStart(2, '0');
    return `FY ${startYY}/${endYY}`;
};

const getFYLabelsForRange = (startDate: Date, endDate: Date, fyStartMonth: number, fyStartDay: number): string[] => {
    if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return [];
    }
    const startFYYear = getFYYearForDate(startDate, fyStartMonth, fyStartDay);
    const endFYYear = getFYYearForDate(endDate, fyStartMonth, fyStartDay);
    const labels: string[] = [];
    for (let y = startFYYear; y <= endFYYear; y++) {
        labels.push(getFYLabelFromYear(y));
    }
    return labels;
};
// ---------------------------------------------------------------------------------------

interface IProjectProgressUpdateFormProps extends IEntityProgressUpdateFormProps<IProject> {
    customerFinancialYear?: ICustomerFinancialYear;
}

export const ProjectProgressUpdateForm = ({ entityId, entity, reportDates, customerFinancialYear, ...other }: IProjectProgressUpdateFormProps): React.ReactElement => {
    const { dataServices, lookupData, loadLookupData: { projectPhases, reportingFrequencies } } = useContext(DataContext);

    useMemo(() => projectPhases(), [projectPhases]);
    useMemo(() => reportingFrequencies(), [reportingFrequencies]);

    // FY start month/day come from the customer's financial year settings, same as ProjectForm.
    const currentFYStart: Date = customerFinancialYear?.fyStartDate
        ? DateService.removeTimezoneOffset(customerFinancialYear.fyStartDate)
        : new Date(new Date().getFullYear(), 0, 1);
    const fyStartMonth = currentFYStart.getMonth();
    const fyStartDay = currentFYStart.getDate();

    const [pendingDateChange, setPendingDateChange] = useState<{
        field: 'StartDate' | 'EndDate';
        date: Date;
        affected: IPPAppProjectUpdateFinancial[];
    } | null>(null);

    const validateProjectProgressUpdate = (pu: IProjectUpdate) => {
        const errors = new ProjectProgressUpdateFormValidations();

        if (!pu.StartDate) {
            errors.StartDate = 'Start Date is required';
            errors.Valid = false;
        } else {
            errors.StartDate = null;
        }

        if (!pu.EndDate) {
            errors.EndDate = 'End Date is required';
            errors.Valid = false;
        } else {
            errors.EndDate = null;
        }

        if (pu.StartDate && pu.EndDate && pu.EndDate < pu.StartDate) {
            errors.EndDate = 'End date cannot be earlier than start date';
            errors.Valid = false;
        }

        return Promise.resolve(errors);
    };

    return (
        <ProgressUpdateForm<IProjectUpdate, ProjectProgressUpdateFormValidations, IProject>
            {...other}
            disableSave={p => p.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0}
            vertoMsg={p => p.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0}
            entityId={entityId}
            entity={entity}
            dueDate={() => reportDates?.Next}
            tags={p => p?.CustomFields?.length > 0 && CustomFieldService.customFieldsToBadgeStrings(p.CustomFields)}
            parents={() => []}
            rag={pu => pu.OverallRagOptionID}
            loadPeople={async p => {
                const people = [];
                if (p) {
                    people.push(...await EntityPeopleService.GetProjectEntityPeople({
                        projectService: dataServices.projectService,
                        userProjectService: dataServices.userProjectService,
                        projectId: p.ID
                    }));
                }
                return people;
            }}
            renderFormFields={(changeHandlers, formState) => {
                const { FormData: pu, LastSignedOffUpdate: so, ValidationErrors: errors } = formState;

                const currentStartDate = DateService.removeTimezoneOffset(pu.StartDate);
                const currentEndDate = DateService.removeTimezoneOffset(pu.EndDate);
                const currentFYLabels = getFYLabelsForRange(currentStartDate, currentEndDate, fyStartMonth, fyStartDay);

                // Financial rows (with an actual entered value) that would fall outside a candidate date range
                const getAffectedFinancials = (startDate: Date, endDate: Date): IPPAppProjectUpdateFinancial[] => {
                    const validLabels = new Set(getFYLabelsForRange(startDate, endDate, fyStartMonth, fyStartDay));
                    const current = pu.PPAppProjectUpdateFinancials || [];
                    return current.filter(f => !validLabels.has(f.FYLabel) && f.FYValue !== null && f.FYValue !== undefined);
                };

                const pruneFinancialsForRange = (startDate: Date, endDate: Date): void => {
                    const validLabels = new Set(getFYLabelsForRange(startDate, endDate, fyStartMonth, fyStartDay));
                    const current = pu.PPAppProjectUpdateFinancials || [];
                    const pruned = current.filter(f => validLabels.has(f.FYLabel));
                    if (pruned.length !== current.length) {
                        changeHandlers.changeJson(pruned, 'PPAppProjectUpdateFinancials');
                    }
                };

                const handleStartDateChange = (date: Date): void => {
                    const affected = getAffectedFinancials(date, currentEndDate);
                    if (affected.length > 0) {
                        setPendingDateChange({ field: 'StartDate', date, affected });
                    } else {
                        changeHandlers.changeDatePicker(date, 'StartDate');
                    }
                };

                const handleEndDateChange = (date: Date): void => {
                    const affected = getAffectedFinancials(currentStartDate, date);
                    if (affected.length > 0) {
                        setPendingDateChange({ field: 'EndDate', date, affected });
                    } else {
                        changeHandlers.changeDatePicker(date, 'EndDate');
                    }
                };

                const handleConfirmDateChange = (): void => {
                    if (!pendingDateChange) return;
                    const { field, date } = pendingDateChange;
                    changeHandlers.changeDatePicker(date, field);
                    if (field === 'StartDate') {
                        pruneFinancialsForRange(date, currentEndDate);
                    } else {
                        pruneFinancialsForRange(currentStartDate, date);
                    }
                    setPendingDateChange(null);
                };

                const handleCancelDateChange = (): void => {
                    setPendingDateChange(null);
                };

                return (
                    <div className={styles.grid}>
                        <ConfirmDialog
                            hidden={!pendingDateChange}
                            title="Remove financial year values?"
                            confirmButtonText="Continue"
                            handleConfirm={handleConfirmDateChange}
                            handleCancel={handleCancelDateChange}
                        >
                            {pendingDateChange &&
                                <div>
                                    <p>Changing this date will remove the following financial year value(s):</p>
                                    <ul>
                                        {[...pendingDateChange.affected]
                                            .sort((a, b) => currentFYLabels.indexOf(a.FYLabel) - currentFYLabels.indexOf(b.FYLabel))
                                            .map(f => (
                                                <li key={f.FYLabel}>{f.FYLabel}: {Number(f.FYValue).toLocaleString('en-GB')}</li>
                                            ))}
                                    </ul>
                                    <p>Continue?</p>
                                </div>
                            }
                        </ConfirmDialog>

                        <div className={styles.gridRow}>
                            <div className={`${styles.gridCol} ${styles.sm12} ${styles.xl4}`}>
                                <CrLabel text="Current phase" singleLine={true} />
                                <CrDropdown
                                    className={styles.formField}
                                    options={LookupService.entitiesToSelectableOptions(lookupData.ProjectPhases)}
                                    selectedKey={pu.ProjectPhaseID}
                                    onChange={(_, o) => changeHandlers.changeDropdown(o, 'ProjectPhaseID')}
                                    history={so.ProjectPhase && so.ProjectPhase.Title}
                                />
                            </div>
                            <div className={`${styles.gridCol} ${styles.sm12} ${styles.xl4}`}>
                                <CrLabel text="Start date" singleLine={true} />
                                <CrDatePicker
                                    className={styles.formField}
                                    value={DateService.removeTimezoneOffset(pu.StartDate)}
                                    onSelectDate={date => handleStartDateChange(date)}
                                    history={so.StartDate}
                                    errorMessage={errors.StartDate}
                                />
                            </div>
                            <div className={`${styles.gridCol} ${styles.sm12} ${styles.xl4}`}>
                                <CrLabel text="End date" singleLine={true} />
                                <CrDatePicker
                                    className={styles.formField}
                                    value={DateService.removeTimezoneOffset(pu.EndDate)}
                                    minDate={DateService.removeTimezoneOffset(pu.StartDate)}
                                    onSelectDate={date => handleEndDateChange(date)}
                                    history={so.EndDate}
                                    errorMessage={errors.EndDate}
                                />
                            </div>
                        </div>
                        <div className={styles.gridRow}>
                            <div className={`${styles.gridCol} ${styles.sm12}`}>
                                <p className={styles.fontSize18}>Headlines</p>
                                <RagPicker
                                    label='Delivery confidence'
                                    className={styles.formField}
                                    selectedRAG={pu.OverallRagOptionID}
                                    onColorChanged={id => changeHandlers.changeColor(id, 'OverallRagOptionID')}
                                    history={so.OverallRagOptionID}
                                />
                                <CrTextField
                                    label="Delivery confidence description"
                                    className={styles.formField}
                                    placeholder='Why has the Senior Responsible Owner chosen the particular RAG rating? Summarise any major risks or issues (put detail in the individual work stream)'
                                    multiline
                                    rows={2}
                                    maxLength={500}
                                    charCounter={true}
                                    value={pu.ProgressUpdate}
                                    onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'ProgressUpdate')}
                                    history={so.ProgressUpdate}
                                />
                                <CrTextField
                                    label="Future actions"
                                    className={styles.formField}
                                    placeholder="What are you doing/expecting others to do? When is the RAG expected to change, and what would cause a change?"
                                    multiline
                                    rows={2}
                                    maxLength={500}
                                    charCounter={true}
                                    value={pu.FutureActions}
                                    onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'FutureActions')}
                                    history={so.FutureActions}
                                />
                                <CrTextField
                                    label="Escalations for senior leader action"
                                    className={styles.formField}
                                    placeholder="What support do you need from your Director General; the Performance, Finance and Risk Committee; the Executive Committee; or ministers?"
                                    multiline
                                    rows={1}
                                    maxLength={500}
                                    charCounter={true}
                                    value={pu.Escalations}
                                    onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Escalations')}
                                    history={so.Escalations}
                                />
                                <p className={styles.fontSize18}>Key Indicators</p>
                                <RagWithComments
                                    label='Finance'
                                    className={styles.formField}
                                    commentsPlaceholder='What is your net position as agreed with your business partner and how does this affect delivery?'
                                    commentsRows={1}
                                    commentsMaxLength={500}
                                    selectedColor={pu.FinanceRagOptionID}
                                    commentValue={pu.FinanceComment}
                                    onColorChanged={colorId => changeHandlers.changeColor(colorId, 'FinanceRagOptionID')}
                                    onCommentChanged={(ev, newValue) => changeHandlers.changeTextField(newValue, 'FinanceComment')}
                                    history={{ color: so.FinanceRagOptionID, comment: so.FinanceComment }}
                                />
                                <RagWithComments
                                    label='People'
                                    className={styles.formField}
                                    commentsPlaceholder='Do you have enough staff or any skills concerns? How will this affect delivery?'
                                    commentsRows={1}
                                    commentsMaxLength={500}
                                    selectedColor={pu.PeopleRagOptionID}
                                    commentValue={pu.PeopleComment}
                                    onColorChanged={colorId => changeHandlers.changeColor(colorId, 'PeopleRagOptionID')}
                                    onCommentChanged={(ev, newValue) => changeHandlers.changeTextField(newValue, 'PeopleComment')}
                                    history={{ color: so.PeopleRagOptionID, comment: so.PeopleComment }}
                                />
                                <RagWithComments
                                    label='Milestones'
                                    className={styles.formField}
                                    commentsPlaceholder='Are your key deliverables likely to happen on time and how does this affect delivery?'
                                    commentsRows={1}
                                    commentsMaxLength={500}
                                    selectedColor={pu.MilestonesRagOptionID}
                                    commentValue={pu.MilestonesComment}
                                    onColorChanged={colorId => changeHandlers.changeColor(colorId, 'MilestonesRagOptionID')}
                                    onCommentChanged={(ev, newValue) => changeHandlers.changeTextField(newValue, 'MilestonesComment')}
                                    history={{ color: so.MilestonesRagOptionID, comment: so.MilestonesComment }}
                                />
                                <RagWithComments
                                    label='Benefits'
                                    className={styles.formField}
                                    commentsPlaceholder='Are the social or financial benefits in the business case likely to be met and how does this affect delivery?'
                                    commentsRows={1}
                                    commentsMaxLength={500}
                                    selectedColor={pu.BenefitsRagOptionID}
                                    commentValue={pu.BenefitsComment}
                                    onColorChanged={colorId => changeHandlers.changeColor(colorId, 'BenefitsRagOptionID')}
                                    onCommentChanged={(ev, newValue) => changeHandlers.changeTextField(newValue, 'BenefitsComment')}
                                    history={{ color: so.BenefitsRagOptionID, comment: so.BenefitsComment }}
                                />
                                <CrCheckbox
                                    className={styles.formField}
                                    label="Mark this project for closure"
                                    checked={pu.ToBeClosed}
                                    onChange={(_e, v) => changeHandlers.changeCheckbox(v, 'ToBeClosed')} />
                                {pu.ToBeClosed &&
                                    <CrTextField className={styles.formField}
                                        label="Why has it been closed?"
                                        required={true} multiline
                                        value={pu.Comment}
                                        maxLength={500}
                                        placeholder='Please provide details of why the project is being closed.'
                                        onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Comment')}
                                        errorMessage={errors.Comment} />}
                                <br />
                            </div>
                        </div>
                    </div>
                );
            }}
            loadDefaultValues={(pu, project) => ({
                ...pu,
                ProjectPhaseID: project.ProjectPhaseID,
                StartDate: project.StartDate,
                EndDate: project.EndDate,
                WholeLifeBenefit: project.TotalBenefits,
                NetPresentValue: project.NetPresentValue,
                WholeLifeCost: (project.PPAppProjectFinancials || []).reduce((sum, f) => sum + (f.FYValue || 0), 0),
                PPAppProjectUpdateFinancials: (project.PPAppProjectFinancials || []).map(f => ({
                    FYLabel: f.FYLabel,
                    FYValue: f.FYValue
                })) as IPPAppProjectUpdateFinancial[]
            })}
            loadEntity={pId => dataServices.projectService.read(pId, true, true)}
            loadEntityUpdate={puId => dataServices.projectUpdateService.read(puId, false, false, ['PPAppProjectUpdateFinancials'])}
            loadNewEntityUpdate={() => new ProjectUpdate(entityId)}
            loadLastSavedProgressUpdate={() => reportDates?.Next &&
                dataServices.projectUpdateService.readLatestUpdateForPeriod(entityId, reportDates.Next)}
            loadLastSignedOffEntityUpdate={() => reportDates?.Previous &&
                dataServices.projectUpdateService.readLastSignedOffUpdateForPeriod(entityId, reportDates.Previous)}
            onValidateUpdate={validateProjectProgressUpdate}
            onBeforeSave={pu => {
                delete pu.Project;
                pu.WholeLifeCost = NumberService.ToNumberOrNull(pu.WholeLifeCost);
                pu.WholeLifeBenefit = NumberService.ToNumberOrNull(pu.WholeLifeBenefit);
                pu.NetPresentValue = NumberService.ToNumberOrNull(pu.NetPresentValue);
            }}
            onSaveUpdate={pu => dataServices.projectUpdateService.create(pu)}
            onClearForm={(p, showForm) => new ProjectProgressUpdateFormState(entityId, reportDates?.Next, p, showForm)}
            reportDates={reportDates}
            reportingFrequencies={lookupData?.ReportingFrequencies}
        />
    );
};